import React, { useEffect, useState } from 'react';
import { getAuditLog, getMissLog, getDeviations } from '../api';
import { AlertTriangle, XCircle, Search } from 'lucide-react';
import { ClassBadge, EntryModeBadge, QualityScore } from './ClassBadge';

type AuditTab = 'all' | 'misses' | 'deviations';

export default function AuditLog() {
  const [sortField, setSortField] = React.useState<string>('created_at');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');
  const handleSort = (f: string) => { if (sortField === f) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('desc'); } };
  const SortHeader = ({ field, label }: { field: string; label: string }) => (
    <th className="text-left p-3 cursor-pointer hover:text-white select-none" onClick={() => handleSort(field)}>
      {label} {sortField === field ? <span className="text-blue-400">{sortDir === 'asc' ? '↑' : '↓'}</span> : null}
    </th>
  );
  const [classFilter, setClassFilter] = useState<string>('all');
  const [tab, setTab] = useState<AuditTab>('all');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const fn = tab === 'misses' ? getMissLog : tab === 'deviations' ? getDeviations : getAuditLog;
        const r = await fn();
        setData(r.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const tabs = [
    { id: 'all', label: 'All Trades' },
    { id: 'misses', label: 'Miss Log' },
    { id: 'deviations', label: 'Deviations' },
  ];

  const filteredData = data.filter((d: any) => classFilter === 'all' || d.signal_class === classFilter);

  return (
    <div className="space-y-4">
      <div className="mb-1"><h1 className="text-xl font-bold">Audit</h1><p className="text-xs text-gray-500 mt-0.5">Miss log and skip analysis. Signals that were filtered out and why they didn't execute. (No hypothetical P&L is shown -- this system has no real price-history data to know what a missed signal would have done.)</p></div>
      <div className="bg-gray-900 rounded-lg border border-gray-800">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Trade Audit Log</h2>
            <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
              <span><span className="text-green-400 font-bold">75+</span> SMR</span>
              <span><span className="text-yellow-400 font-bold">50+</span> Macro+Liq</span>
              <span><span className="text-orange-400 font-bold">30+</span> Macro</span>
              <span><span className="text-gray-500 font-bold">5</span> Breaker</span>
            </div>
          </div>
          <div className="flex gap-1">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id as AuditTab)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${tab === t.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {tab === 'misses' && (
          <div className="p-3 bg-yellow-900/20 border-b border-yellow-900/30 flex items-center gap-2">
            <Search size={14} className="text-yellow-400" />
            <span className="text-xs text-yellow-300">Setups that met 2 of 3 conditions but did not execute — review if rules are too strict</span>
          </div>
        )}
        {tab === 'deviations' && (
          <div className="p-3 bg-red-900/20 border-b border-red-900/30 flex items-center gap-2">
            <AlertTriangle size={14} className="text-red-400" />
            <span className="text-xs text-red-300">Trades that closed at stop — review if stop placement needs adjustment</span>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-32 text-gray-500">Loading...</div>
        ) : (
          <>
          <div className="px-4 py-2 border-b border-gray-800 flex justify-end"><select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-3 py-1 text-xs"><option value="all">All Classes</option><option value="SMR">SMR</option><option value="MACRO_LIQ">MACRO_LIQ</option><option value="MACRO_ONLY">MACRO_ONLY</option><option value="BREAKER_ONLY">BREAKER_ONLY</option><option value="TREND_CONT">TREND_CONT</option><option value="SECOND_CHANCE">SECOND_CHANCE</option></select></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <SortHeader field="created_at" label="Time" />
                  <SortHeader field="instrument" label="Instrument" />
                  <SortHeader field="signal_class" label="Class" />
                  <SortHeader field="direction" label="Direction" />
                  <th className="text-left p-3" title="Quality score based on confluence factors">Quality</th>
                  <th className="text-left p-3" title="Did liquidity, SMT, and breaker fire in correct order?">Sequence</th>
                  <th className="text-left p-3" title="Which STDV target was hit, or if stop was hit">Exit</th>
                  <SortHeader field="pnl" label="P&L" />
                  <th className="text-left p-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-gray-600 p-8">No records yet</td></tr>
                ) : filteredData.map((a: any) => (
                  <tr key={a.id} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                    <td className="p-3 text-gray-400 text-xs">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="p-3 font-medium">{a.instrument}</td>
                    <td className="p-3 whitespace-nowrap">
                      <ClassBadge signalClass={a.signal_class} />
                      <EntryModeBadge mode={a.entry_mode} />
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${a.direction === 'long' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
                        {a.direction?.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3">
                      <QualityScore score={typeof a.quality_score === 'number' ? a.quality_score : (parseInt(a.quality_score) || 0)} />
                    </td>
                    <td className="p-3"><span className={`text-xs px-1.5 py-0.5 rounded ${a.exit_strategy === 'simple' ? 'bg-blue-900 text-blue-300' : a.exit_strategy === 'scaled' ? 'bg-purple-900 text-purple-300' : 'bg-gray-700 text-gray-500'}`}>{a.exit_strategy || '—'}</span></td>
                    <td className="p-3">
                      {a.sequence_valid ? '✅' : a.miss_log ? <span className="text-yellow-400 text-xs">MISS</span> : '❌'}
                    </td>
                    <td className="p-3">
                      {a.deviation_flag ? (
                        <span className="text-red-400 text-xs flex items-center gap-1"><XCircle size={12} /> {a.exit_type}</span>
                      ) : a.stdv_target_hit ? (
                        <span className="text-green-400 text-xs">STDV {a.stdv_target_hit}</span>
                      ) : a.miss_log ? (
                        // Miss rows have no real exit -- the signal was never taken.
                        // (Previously showed a fabricated would_hit_stdv2/would_stop value.)
                        <span className="text-gray-500 text-xs">n/a</span>
                      ) : (
                        <span className="text-gray-500 text-xs">{a.exit_type || 'open'}</span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs">
                      {a.miss_log ? (
                        // 2026-07-21: this column used to render a fabricated "hypothetical
                        // reward/risk" figure derived from an assumed-always-optimistic
                        // exit price on missed signals (no real price-history exists in
                        // this system to know what a missed signal would have actually
                        // done -- see BACKTEST_HARNESS_REPORT.md section 5, item 2).
                        // That figure has been removed rather than fixed. Miss rows have
                        // no real P&L to show, so we say so plainly instead of guessing.
                        <span className="text-gray-500">not observed</span>
                      ) : (
                        <span className={`font-bold ${(a.pnl || 0) > 0 ? 'text-green-400' : (a.pnl || 0) < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                          {a.pnl != null ? `${(a.pnl || 0) >= 0 ? '+' : ''}$${(a.pnl || 0).toFixed(2)}` : '—'}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-gray-500 max-w-xs truncate" title={a.notes}>{a.notes && a.notes !== 'none' ? a.notes : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
        )}
      </div>
    </div>
  );
}
